// The calendar's left panel — the live map's panel pattern (LiveMapPanel):
// four count tiles, then ONE white sheet holding the tabs and their lists.
// To fix (what needs action), Bookings (the day's sessions, by state) and Team
// (everyone scheduled, by hub).
//
// Selecting anything on the board turns the sheet into that thing's details,
// with Back — the tiles stay, so the day's numbers never leave the screen.

import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bednbody/ui/components/tabs";
import { cn } from "@bednbody/ui/lib/utils";
import { CalendarCheckIcon, CheckCircleIcon, UsersThreeIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import {
  ListCard,
  ListRow,
  PanelEmpty,
  SectionLabel,
  StatTileButton,
  StatusPill,
  TimeBlock,
} from "../live-map/LiveMapCards";
import {
  type BlockPhase,
  type BoardSection,
  clockLabel,
  type SessionBlock,
  serviceLabel,
  spanLabel,
} from "./calendar-board";
import type { CalendarFixItem, CalendarTiles } from "./calendar-board.fix";
import {
  PHASE_LABEL,
  PHASE_TONE,
  personStatusLabel,
  personStatusTone,
} from "./calendar-board.labels";
import { BADGE, BadgeImage, StaffAvatar } from "./CalendarIcons";

export type CalendarPanelTab = "fix" | "bookings" | "team";
export type CalendarTile = keyof CalendarTiles;

const TILES: readonly {
  key: CalendarTile;
  label: string;
  hint: string;
  image: string;
  alarm?: boolean;
}[] = [
  {
    key: "needTherapist",
    label: "Unassigned",
    hint: "no therapist",
    image: BADGE.needsTherapist,
    alarm: true,
  },
  { key: "notClockedIn", label: "Not in", hint: "no clock-in", image: BADGE.notClockedIn },
  { key: "sessionsNow", label: "In session", hint: "right now", image: BADGE.inSession },
  { key: "freeNextHour", label: "Free soon", hint: "next hour", image: BADGE.free },
];

/** Each To-fix kind's badge, by the item id's prefix (calendar-board.fix). */
const FIX_BADGE: Record<string, string> = {
  sos: BADGE.sos,
  assign: BADGE.needsTherapist,
  offline: BADGE.disconnected,
  clock: BADGE.notClockedIn,
  hour: BADGE.inSession,
};

/** A row's pill — short, so the booking's name keeps the room. */
const SHORT_PHASE: Record<BlockPhase, string> = {
  unassigned: "No therapist",
  now: "Now",
  next: "Next",
  done: "Done",
};

/** The Bookings tab reads top-down like the day: what is stuck, what is on, what is next. */
const PHASE_ORDER: readonly BlockPhase[] = ["unassigned", "now", "next", "done"];

interface CalendarPanelProps {
  tiles: CalendarTiles;
  onTile: (tile: CalendarTile) => void;
  tab: CalendarPanelTab;
  onTabChange: (tab: CalendarPanelTab) => void;
  fixes: readonly CalendarFixItem[];
  onOpenFix: (item: CalendarFixItem) => void;
  /** The day's sessions after the hub filter and search. */
  sessions: readonly SessionBlock[];
  onOpenSession: (session: SessionBlock) => void;
  /** The board's sections after every filter. */
  team: readonly BoardSection[];
  onOpenPerson: (staffId: string) => void;
  staffName: (staffId: string | null) => string;
  hubName: (hubId: string) => string;
  /** When set, the sheet shows this instead of the tabs. */
  detail: ReactNode | null;
  selectedBookingId: string | null;
  selectedStaffId: string | null;
}

function TabCount({ value, alarm = false }: { value: number; alarm?: boolean }) {
  return (
    <span
      className={cn(
        "rounded px-1.5 text-xs font-semibold tabular-nums",
        alarm ? "bg-destructive text-primary-foreground" : "bg-muted text-muted-foreground",
      )}
    >
      {value}
    </span>
  );
}

export function CalendarPanel({
  tiles,
  onTile,
  tab,
  onTabChange,
  fixes,
  onOpenFix,
  sessions,
  onOpenSession,
  team,
  onOpenPerson,
  staffName,
  hubName,
  detail,
  selectedBookingId,
  selectedStaffId,
}: CalendarPanelProps) {
  const teamCount = team.reduce((sum, section) => sum + section.rows.length, 0);
  return (
    <SurfaceCard
      className="flex min-h-0 w-88 shrink-0 flex-col gap-4 overflow-hidden rounded-xl border border-border bg-background p-4 shadow-card"
      render={<aside aria-label="Calendar panel" />}
    >
      <div className="grid grid-cols-2 gap-3">
        {TILES.map((tile) => (
          <StatTileButton
            alarm={tile.alarm}
            hint={tile.hint}
            image={tile.image}
            key={tile.key}
            label={tile.label}
            onPress={() => onTile(tile.key)}
            pressed={false}
            value={tiles[tile.key]}
          />
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-border bg-card p-1 shadow-sm">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border/70 bg-card">
          {detail ?? (
            <Tabs
              className="flex min-h-0 flex-1 flex-col gap-0"
              onValueChange={(value) => onTabChange(value as CalendarPanelTab)}
              value={tab}
            >
              <div className="border-b border-border/70 p-1">
                <TabsList className="w-full rounded-lg bg-muted/50 p-1 group-data-horizontal/tabs:h-9">
                  {(
                    [
                      ["fix", "To fix", fixes.length, fixes.some((item) => item.tone === "danger")],
                      ["bookings", "Bookings", sessions.length, false],
                      ["team", "Team", teamCount, false],
                    ] as const
                  ).map(([value, label, count, alarm]) => (
                    <TabsTrigger
                      className="gap-1.5 rounded-md font-semibold data-active:bg-card data-active:shadow-sm data-active:ring-1 data-active:ring-border"
                      key={value}
                      value={value}
                    >
                      {label}
                      <TabCount alarm={alarm} value={count} />
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              <TabsContent className="min-h-0 flex-1 overflow-y-auto" value="fix">
                {fixes.length === 0 ? (
                  <PanelEmpty
                    description="Bookings without a therapist, missed clock-ins and fully booked hours show up here."
                    icon={CheckCircleIcon}
                    title="All good"
                  />
                ) : (
                  <ListCard>
                    {fixes.map((item) => (
                      <ListRow
                        key={item.id}
                        leading={
                          <BadgeImage
                            size="md"
                            src={FIX_BADGE[item.id.slice(0, item.id.indexOf(":"))] ?? BADGE.free}
                          />
                        }
                        meta={`${item.action} →`}
                        onSelect={() => onOpenFix(item)}
                        subtitle={item.detail}
                        title={item.title}
                        trailing={
                          <StatusPill
                            label={item.tone === "danger" ? "Urgent" : "Check"}
                            tone={item.tone}
                          />
                        }
                      />
                    ))}
                  </ListCard>
                )}
              </TabsContent>

              <TabsContent className="min-h-0 flex-1 overflow-y-auto pb-2" value="bookings">
                {sessions.length === 0 ? (
                  <PanelEmpty
                    description="Sessions booked for this day show here."
                    icon={CalendarCheckIcon}
                    title="No bookings"
                  />
                ) : null}
                {PHASE_ORDER.map((phase) => {
                  const rows = sessions.filter((session) => session.phase === phase);
                  if (rows.length === 0) return null;
                  return (
                    <section key={phase}>
                      <SectionLabel count={rows.length} label={PHASE_LABEL[phase]} />
                      <ListCard>
                        {rows.map((session) => (
                          <ListRow
                            key={session.id}
                            leading={<TimeBlock time={clockLabel(session.startMin)} />}
                            onSelect={() => onOpenSession(session)}
                            selected={session.bookingId === selectedBookingId}
                            subtitle={`${session.ref} · ${hubName(session.hubId)}`}
                            title={
                              session.therapistId
                                ? `${serviceLabel(session)} · ${staffName(session.therapistId)}`
                                : serviceLabel(session)
                            }
                            trailing={
                              <StatusPill label={SHORT_PHASE[phase]} tone={PHASE_TONE[phase]} />
                            }
                          />
                        ))}
                      </ListCard>
                    </section>
                  );
                })}
              </TabsContent>

              <TabsContent className="min-h-0 flex-1 overflow-y-auto pb-2" value="team">
                {teamCount === 0 ? (
                  <PanelEmpty
                    description="Nobody matches these filters. Clear the search or hub to see everyone."
                    icon={UsersThreeIcon}
                    title="Nobody to show"
                  />
                ) : null}
                {team.map((section) => (
                  <section key={section.hubId}>
                    <SectionLabel count={section.rows.length} label={`${section.hubName} hub`} />
                    <ListCard>
                      {section.rows.map((row) => (
                        <ListRow
                          key={row.staffId}
                          leading={<StaffAvatar role={row.role} status={row.status} />}
                          onSelect={() => onOpenPerson(row.staffId)}
                          rowId={row.staffId}
                          selected={row.staffId === selectedStaffId}
                          subtitle={[
                            row.role === "rider" ? "Rider" : "Therapist",
                            row.shift ? spanLabel(row.shift) : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          title={row.name}
                          trailing={
                            <StatusPill
                              label={personStatusLabel(row.status)}
                              tone={personStatusTone(row.status)}
                            />
                          }
                        />
                      ))}
                    </ListCard>
                  </section>
                ))}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </SurfaceCard>
  );
}
